import { firebaseAuth } from './firebase';

export type MissingField = { path: string; message: string };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    // Structured list of missing fields for lifecycle validation errors
    // (server/lib/lifecycleValidation.ts). UI surfaces as per-field bullets.
    public readonly missing: MissingField[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ⚠️⚠️ CLOUDFLARE 524 — "the origin took too long to answer" — is NOT a
// failure of the request. Cloudflare gives up on the connection at ~100s while
// Cloud Run (`--timeout=600`) keeps running the handler to completion. So a 524
// means "the answer was LOST", and the work is usually still happening, or
// already done.
//
// That distinction is load-bearing because the natural reaction to an error is
// to press Retry, and every long endpoint here does real work: on 2026-09-28 a
// 48-row "Create Meta Asset Folders" run 524'd, finished correctly on the
// server, and the Retry it invited DUPLICATED the rollout's Drive folder tree
// (see server/modules/rollouts/createFoldersService.ts for why a retry can do
// that). The same shape applies to a preview send retry (duplicate SMS).
//
// Handled HERE, once, rather than per endpoint: a 524 means the same thing
// everywhere, so this covers the next long endpoint too. Surfaces that render
// a Retry control check `isStillRunningError` / `canRetryAfter` and suppress it.
//
// ⚠️ ONLY 524. Every other status keeps its ordinary message. A 400 for an
// unconfigured brand Drive folder is a real failure needing a real fix, and a
// wrapper that told operators "it's probably still running" about everything
// would teach them to ignore errors — worse than the errors.
//
// ⚠️ 502/503/504 are deliberately NOT folded in: those can mean the instance
// died or never received the request, where the work did NOT happen and a
// retry is the right move. Only 524 is specifically "origin still working".
export const CLOUDFLARE_ORIGIN_TIMEOUT = 524;

export const STILL_RUNNING_MESSAGE =
  'This is still running on the server — the connection timed out before it finished, ' +
  'but the work carries on. Wait about 30 seconds and refresh the page to see the result. ' +
  "Don't run it again in the meantime.";

export class StillRunningError extends ApiError {
  constructor() {
    super(CLOUDFLARE_ORIGIN_TIMEOUT, STILL_RUNNING_MESSAGE);
    this.name = 'StillRunningError';
  }
}

export function isStillRunningError(err: unknown): boolean {
  return err instanceof ApiError && err.status === CLOUDFLARE_ORIGIN_TIMEOUT;
}

// Whether a surface should offer Retry after this error. False ONLY for the
// 524 case; every other failure keeps its existing retry affordance.
export function canRetryAfter(err: unknown): boolean {
  return !isStillRunningError(err);
}

// The ONE translation from a non-OK response to an ApiError, shared by every
// fetch wrapper that throws ApiError (the api() helper below plus the modules
// that need a raw fetch for a non-JSON success body). A 524 body is
// Cloudflare's HTML error page, so it never reaches the JSON parse.
export async function apiErrorFromResponse(res: Response): Promise<ApiError> {
  if (res.status === CLOUDFLARE_ORIGIN_TIMEOUT) return new StillRunningError();
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    missing?: MissingField[];
  };
  const missing = Array.isArray(body.missing) ? body.missing : [];
  return new ApiError(res.status, body.error ?? `HTTP ${res.status}`, missing);
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const current = firebaseAuth.currentUser;
  if (!current) throw new ApiError(401, 'Not signed in');
  const token = await current.getIdToken();

  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) throw await apiErrorFromResponse(res);

  return res.json() as Promise<T>;
}
